import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { fmtRange } from "../../lib/time";
import { LegalModal } from "../LegalModal";
import { LEGAL } from "../../constants/legal";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const cubrePaquete = (covers, kind) => covers === "any" || covers === kind;

/**
 * Reparte paquete y clases de regalo entre las clases del carrito, en el mismo
 * orden y con las mismas reglas que el servidor. Aquí solo sirve para enseñar
 * el precio antes de pagar: quien manda es el servidor.
 */
export function repartirCarrito(items, { membership, usarPaquete, creditos, usarCreditos, descuentoPct, soloUnaClase }) {
  let restantes = membership && !membership.unlimited ? membership.classes_remaining : 0;
  let libres = creditos;
  let descuentoPendiente = true; // el de bienvenida solo rebaja una clase
  const lineas = [];

  const ordenados = [...items].sort((a, b) =>
    (a.cls.date + a.cls.time).localeCompare(b.cls.date + b.cls.time)
  );

  for (const it of ordenados) {
    const n = it.bikes.length;
    const kind = it.cls.kind === "floor" ? "floor" : "ride";
    const precio = it.cls.price;

    const vale = !!(usarPaquete && membership && membership.active && cubrePaquete(membership.covers, kind));
    let memCover = 0;
    if (vale && membership.unlimited) memCover = Math.min(1, n);
    else if (vale) memCover = Math.min(restantes, n);
    if (vale && !membership.unlimited) restantes -= memCover;

    const sinCubrir = n - memCover;
    const gratis = kind === "ride" && usarCreditos && libres >= 1 && sinCubrir >= 1 ? 1 : 0;
    libres -= gratis;

    const cobradas = sinCubrir - gratis;
    const subtotal = cobradas * precio;
    let base = subtotal;
    if (soloUnaClase) {
      base = descuentoPendiente && cobradas > 0 ? Math.min(precio, subtotal) : 0;
      if (cobradas > 0) descuentoPendiente = false;
    }
    const descuento = Math.round(base * descuentoPct) / 100;

    lineas.push({
      it,
      kind,
      memCover,
      gratis,
      cobradas,
      gross: n * precio,
      membershipAmount: memCover * precio,
      freeAmount: gratis * precio,
      subtotal,
      descuento,
      total: subtotal - descuento,
    });
  }

  const suma = (k) => lineas.reduce((x, l) => x + l[k], 0);
  return {
    lineas,
    gross: suma("gross"),
    membershipAmount: suma("membershipAmount"),
    freeAmount: suma("freeAmount"),
    descuento: suma("descuento"),
    total: suma("total"),
    memCover: suma("memCover"),
    gratis: suma("gratis"),
  };
}

export const CheckoutPanel = ({ items, onSuccess, onConflict }) => {
  const { lang, t } = useLang();
  const b = t.booking;
  const { isAuth, account: authAccount, refresh } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [couponMsg, setCouponMsg] = useState("");
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [account, setAccount] = useState(null);
  const [useFree, setUseFree] = useState(true);
  const [useMembership, setUseMembership] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [payments, setPayments] = useState({ enabled: false });
  const [paquetes, setPaquetes] = useState([]);

  useEffect(() => {
    axios.get(`${API}/packages`).then((r) => setPaquetes(r.data || [])).catch(() => {});
  }, []);

  // ¿Está activa la pasarela de pago?
  useEffect(() => {
    axios.get(`${API}/payments/config`).then((r) => setPayments(r.data)).catch(() => {});
  }, []);

  // Prellena nombre y correo si hay sesión iniciada.
  useEffect(() => {
    if (isAuth && authAccount) {
      setName((prev) => prev || authAccount.profile.name || "");
      setEmail((prev) => prev || authAccount.profile.email || "");
    }
  }, [isAuth, authAccount]);

  // Cuenta por correo: clases gratis y progreso de fidelidad (para invitados y clientes).
  useEffect(() => {
    const e = email.trim().toLowerCase();
    if (!e.includes("@") || !e.includes(".")) {
      setAccount(null);
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      axios
        .get(`${API}/customers/${encodeURIComponent(e)}`)
        .then((r) => alive && setAccount(r.data))
        .catch(() => alive && setAccount(null));
    }, 500);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [email]);

  const hayRide = items.some((i) => i.cls.kind !== "floor");
  const haySuelo = items.some((i) => i.cls.kind === "floor");

  // El paquete solo aplica si es el titular reservando con su propio correo.
  const emailMatchesAccount =
    isAuth && authAccount && email.trim().toLowerCase() === (authAccount.profile.email || "");
  const membership = emailMatchesAccount ? authAccount.membership : null;
  const memUsable = !!(
    membership &&
    membership.active &&
    items.some((i) => cubrePaquete(membership.covers, i.cls.kind === "floor" ? "floor" : "ride"))
  );

  const freeCredits = account && hayRide ? account.free_credits : 0;

  const cuenta = useMemo(
    () =>
      repartirCarrito(items, {
        membership: useMembership ? membership : null,
        usarPaquete: useMembership && memUsable,
        creditos: freeCredits,
        usarCreditos: useFree,
        descuentoPct: applied ? applied.discount_pct : 0,
        soloUnaClase: !!(applied && applied.first_class),
      }),
    [items, membership, memUsable, useMembership, freeCredits, useFree, applied]
  );

  const { total, gross, membershipAmount, freeAmount, descuento, memCover, gratis } = cuenta;
  const discountPct = applied ? applied.discount_pct : 0;

  // Precio por clase del mejor paquete que sirva para lo que hay en el carrito.
  const kindsCarrito = new Set(items.map((i) => (i.cls.kind === "floor" ? "floor" : "ride")));
  const utiles = paquetes.filter(
    (p) => p.classes > 0 && [...kindsCarrito].some((k) => cubrePaquete(p.covers, k))
  );
  const mejorPorClase = utiles.length ? Math.min(...utiles.map((p) => p.price / p.classes)) : null;
  const precioMedio = items.length ? gross / items.reduce((n, i) => n + i.bikes.length, 0) : 0;

  const cubreAlgoDelCarrito = (covers) =>
    [...kindsCarrito].some((k) => cubrePaquete(covers, k));

  const validarCupon = useCallback(
    async (codigo, correo) => {
      if (!codigo.trim()) return;
      const res = await axios.post(`${API}/coupons/validate`, { code: codigo, email: correo });
      if (res.data.valid) {
        setApplied(res.data);
        setCouponMsg(
          res.data.first_class
            ? `${res.data.code} — ${res.data.discount_pct}% ${b.couponOneClass}`
            : `${res.data.code} — ${res.data.discount_pct}% ${b.couponApplied}`
        );
      } else {
        setApplied(null);
        setCouponMsg(res.data.reason === "first_class_only" ? b.couponFirstOnly : b.couponInvalid);
      }
    },
    [b]
  );

  const applyCoupon = () => validarCupon(coupon, email);

  // El descuento depende del correo. Si lo cambia después de aplicarlo, hay que
  // volver a preguntar: si no, enseñaríamos un precio que luego no se le cobra.
  useEffect(() => {
    if (!applied) return;
    const id = setTimeout(() => validarCupon(applied.code, email), 500);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const pay = async () => {
    setError("");
    if (!items.length) return setError(b.cartEmpty);
    if (!name.trim() || !email.includes("@")) return setError(b.errRequired);
    if (!acceptedTerms) return setError(b.errTerms);
    setProcessing(true);
    try {
      await sleep(300);
      const res = await axios.post(`${API}/bookings/lote`, {
        items: items.map((i) =>
          i.cls.kind === "floor"
            ? { class_id: i.cls.id, spots: i.bikes.length }
            : { class_id: i.cls.id, bikes: i.bikes }
        ),
        customer_name: name,
        email,
        coupon_code: applied ? applied.code : null,
        use_free_credit: gratis > 0,
        use_membership: memCover > 0,
        terms_accepted: true,
        lang, // para mandarle el recibo en su idioma
      });
      // Con Stripe activo el pago ocurre en la página segura de Stripe.
      if (res.data.requires_payment && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      if (isAuth && refresh) refresh();
      onSuccess(res.data);
    } catch (e) {
      if (e.response && e.response.status === 409) {
        setError(e.response.data?.detail || b.errConflict);
        onConflict(e.response.data?.class_id);
      } else {
        setError(e.response?.data?.detail || "Error");
      }
    } finally {
      setProcessing(false);
    }
  };

  const nSitios = items.reduce((n, i) => n + i.bikes.length, 0);
  const soloBicis = items.length > 0 && items.every((i) => i.cls.kind !== "floor");
  const palabraSitios =
    nSitios === 1 ? (soloBicis ? b.bikeWord : b.seatWord) : soloBicis ? b.bikesWord : b.seatsWord;

  return (
    <div className="bg-[#0C0C0C] border border-[#232323] p-8" data-testid="checkout-panel">
      <p className="overline-gold">{b.summary}</p>

      {/* Las clases del carrito */}
      <div className="mt-6 pb-6 border-b border-white/10 space-y-4" data-testid="checkout-items">
        {items.length === 0 ? (
          <p className="text-xs text-zinc-600 italic">{b.cartEmpty}</p>
        ) : (
          cuenta.lineas.map(({ it, kind, memCover: mc, gratis: g, total: tot }) => (
            <div key={it.cls.id} className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="font-serif text-base text-white truncate">{it.cls.name[lang]}</p>
                <p className="mt-0.5 text-xs text-zinc-500 font-light">
                  {it.cls.date} · {fmtRange(it.cls.time, it.cls.time_end)}
                </p>
                <p className="mt-1 text-[0.65rem] uppercase tracking-[0.2em] text-zinc-600">
                  {kind === "floor"
                    ? `${it.bikes.length} ${it.bikes.length === 1 ? b.spotLabel : b.spotsLabel}`
                    : `${it.bikes.length === 1 ? b.bike : b.bikes} ${it.bikes.join(", ")}`}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm text-white">${tot.toFixed(2)}</p>
                {mc > 0 && <p className="text-[0.6rem] text-[#C5A059] mt-0.5">🎟️ {b.membershipLine}</p>}
                {g > 0 && <p className="text-[0.6rem] text-[#C5A059] mt-0.5">🎁 {b.freeClassLine}</p>}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-6 space-y-2 text-sm">
        <div className="flex justify-between text-zinc-400">
          <span>
            {nSitios} {palabraSitios} · {items.length}{" "}
            {items.length === 1 ? b.classWord : b.classesWord}
          </span>
          <span data-testid="checkout-subtotal">${gross.toFixed(2)}</span>
        </div>
        {memCover > 0 && (
          <div className="flex justify-between text-[#C5A059]">
            <span>🎟️ {b.membershipLine} ({memCover})</span>
            <span data-testid="checkout-membership">−${membershipAmount.toFixed(2)}</span>
          </div>
        )}
        {gratis > 0 && (
          <div className="flex justify-between text-[#C5A059]">
            <span>🎁 {b.freeClassLine} ({gratis})</span>
            <span data-testid="checkout-free">−${freeAmount.toFixed(2)}</span>
          </div>
        )}
        {applied && (
          <div className="flex justify-between text-[#C5A059]">
            <span>{b.discount} ({discountPct}%)</span>
            <span data-testid="checkout-discount">−${descuento.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between text-white font-medium text-lg pt-2 border-t border-white/10">
          <span>{b.total}</span>
          <span data-testid="checkout-total">${total.toFixed(2)}</span>
        </div>
      </div>

      {memUsable && (
        <label
          data-testid="membership-toggle"
          className="mt-5 flex items-center gap-3 cursor-pointer rounded-sm border border-[#C5A059]/40 bg-[#C5A059]/5 px-4 py-3"
        >
          <input
            type="checkbox"
            checked={useMembership}
            onChange={(e) => setUseMembership(e.target.checked)}
            className="accent-[#C5A059] h-4 w-4"
          />
          <span className="text-xs text-[#C5A059] leading-relaxed">
            🎟️ {b.membershipUse}
            {!membership.unlimited && ` (${membership.classes_remaining} ${b.membershipLeft})`}
            {membership.unlimited && ` — ${t.account.unlimited}`}
          </span>
        </label>
      )}

      {/* Tiene paquete pero reserva sin sesión: sin este aviso pagaría dos veces. */}
      {!memUsable && account && account.has_membership && cubreAlgoDelCarrito(account.membership_covers) && (
        <div data-testid="membership-signin-note" className="mt-5 border border-[#C5A059]/40 bg-[#C5A059]/[0.06] px-4 py-3">
          <p className="text-xs text-[#C5A059] leading-relaxed">🎟️ {b.hasPackageSignIn}</p>
          <Link to="/account" className="mt-2 inline-block text-[0.65rem] uppercase tracking-[0.25em] text-[#C5A059] underline underline-offset-4">
            {b.signInToUse}
          </Link>
        </div>
      )}

      {/* Si paga suelto y el paquete le saldría mejor, se le dice. */}
      {!memUsable && !(account && account.has_membership) && mejorPorClase && mejorPorClase < precioMedio && (
        <p data-testid="package-hint" className="mt-5 text-[0.7rem] text-zinc-500 leading-relaxed">
          {b.packageHint.replace("{p}", `$${mejorPorClase.toFixed(2)}`)}{" "}
          <Link to="/#pricing" className="text-[#C5A059] underline underline-offset-4">
            {b.seePackages}
          </Link>
        </p>
      )}

      {haySuelo && (
        <p data-testid="floor-class-note" className="mt-5 text-[0.7rem] text-zinc-500 leading-relaxed">
          {b.floorNote}
        </p>
      )}

      {freeCredits > 0 ? (
        <label
          data-testid="free-class-toggle"
          className="mt-5 flex items-center gap-3 cursor-pointer rounded-sm border border-[#C5A059]/40 bg-[#C5A059]/5 px-4 py-3"
        >
          <input
            type="checkbox"
            checked={useFree}
            onChange={(e) => setUseFree(e.target.checked)}
            className="accent-[#C5A059] h-4 w-4"
          />
          <span className="text-xs text-[#C5A059] leading-relaxed">🎁 {b.freeClassAvailable}</span>
        </label>
      ) : (
        account &&
        hayRide &&
        account.classes_booked > 0 && (
          <p data-testid="reward-progress" className="mt-5 text-[0.7rem] text-zinc-500 leading-relaxed">
            {b.rewardProgress.replace("{n}", account.classes_to_next_reward)}
          </p>
        )
      )}

      {/* Su descuento de bienvenida: lo gasta aquí o se lo guarda para otra clase. */}
      {!applied && account?.welcome_coupon?.available && (
        <div
          data-testid="welcome-discount"
          className="mt-5 border border-[#C5A059]/40 bg-[#C5A059]/[0.07] px-4 py-4"
        >
          <p className="text-xs text-[#C5A059] leading-relaxed">
            🎉 {b.haveDiscount}
          </p>
          <p className="mt-1.5 text-[0.7rem] text-zinc-400 leading-relaxed">{b.haveDiscountNote}</p>
          <button
            type="button"
            onClick={() => {
              setCoupon(account.welcome_coupon.code);
              validarCupon(account.welcome_coupon.code, email);
            }}
            data-testid="use-welcome-discount"
            className="mt-3 border border-[#C5A059]/60 px-6 py-2.5 text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors duration-500"
          >
            {b.useDiscount}
          </button>
        </div>
      )}

      <div className="mt-6 flex gap-2">
        <input
          data-testid="coupon-input"
          className="input-dark flex-1"
          placeholder={b.coupon}
          value={coupon}
          onChange={(e) => setCoupon(e.target.value)}
        />
        <button data-testid="coupon-apply-btn" onClick={applyCoupon} className="btn-gold !px-5 !py-3 !text-[0.6rem]">
          {b.apply}
        </button>
      </div>
      {couponMsg && (
        <p data-testid="coupon-message" className={`mt-2 text-xs ${applied ? "text-[#C5A059]" : "text-red-400"}`}>
          {couponMsg}
        </p>
      )}

      <div className="mt-8 space-y-3">
        <input data-testid="customer-name-input" className="input-dark" placeholder={b.name} value={name} onChange={(e) => setName(e.target.value)} />
        <input data-testid="customer-email-input" className="input-dark" placeholder={b.email} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>

      {total > 0 && payments.enabled && (
        <p data-testid="secure-payment-note" className="mt-5 flex items-start gap-2 text-[0.7rem] text-zinc-500 leading-relaxed">
          <span className="text-[#C5A059] mt-[1px]">🔒</span>
          <span>{b.securePay}</span>
        </p>
      )}

      <label data-testid="terms-checkbox" className="mt-6 flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
          className="mt-[3px] accent-[#C5A059] h-4 w-4 shrink-0"
        />
        <span className="text-xs text-zinc-400 leading-relaxed">
          {b.acceptTerms}{" "}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
            className="text-[#C5A059] underline underline-offset-2 hover:text-[#E4CE9A]"
          >
            {b.termsLink}
          </button>
          .
        </span>
      </label>

      {error && (
        <p data-testid="checkout-error" className="mt-4 text-xs text-red-400">
          {error}
        </p>
      )}

      <button
        data-testid="pay-btn"
        onClick={pay}
        disabled={processing || !items.length}
        className="btn-gold w-full mt-8 !py-4 text-center disabled:opacity-40"
      >
        {processing
          ? b.processing
          : total > 0 && payments.enabled
          ? `${b.payStripe} · $${total.toFixed(2)}`
          : `${b.pay} · $${total.toFixed(2)}`}
      </button>
      {!payments.enabled && <p className="mt-4 text-[0.65rem] text-zinc-600 text-center">{b.paymentsOff}</p>}

      <LegalModal doc={showTerms ? LEGAL.terms[lang] : null} onClose={() => setShowTerms(false)} />
    </div>
  );
};
